import { HttpClient } from "@angular/common/http";
import { ServiceBase } from "../base/service.base";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { ApolloQueryResult } from "@apollo/client/core";
import { map, catchError } from "rxjs/operators";
import { Notification } from "../model/user/notification";
import {
  GetUsersGQL,
  GetUsersQuery,
  GetUsersQueryVariables,
  User,
} from "../../generated/graphql";
import {
  GetUserByCommitteeGQL,
  GetUserByCommitteeQuery,
} from "src/generated/graphql/users/users.graphql";

@Injectable({
  providedIn: "root",
})
export class UserService extends ServiceBase {
  isAuthenticated = false;

  private userDataSource = new BehaviorSubject<User>(new User());
  user = this.userDataSource.asObservable();

  private languageSource = new BehaviorSubject<string>("");
  language = this.languageSource.asObservable();

  private notificationDataSource = new BehaviorSubject<Notification[]>([]);
  notification = this.notificationDataSource.asObservable();

  constructor(
    http: HttpClient,
    private getUsersGQL: GetUsersGQL,
    private getUserByCommitteeGQL: GetUserByCommitteeGQL,
  ) {
    super(http);
  }

  getStatusAuthenticated() {
    const token = sessionStorage.getItem("token") as string;
    if (token != null) {
      this.isAuthenticated = true;
    } else {
      this.isAuthenticated = false;
    }
    return this.isAuthenticated;
  }

  getAuthenticate(model) {
    const url = `${this.BASE_URL}/user/authenticate`;
    return this.http.post(url, model);
  }

  search(page, filter) {
    const url = `${this.BASE_URL}/user/search/${page}`;
    return this.http.post(url, filter);
  }

  getMe() {
    const url = `${this.BASE_URL}/user/me`;
    return this.http.get(url).subscribe({
      next: (data) => {
        this.userDataSource.next(data as User);
        this.getUserLocal();
      },
    });
  }

  create(model) {
    const url = `${this.BASE_URL}/user`;
    return this.http.post(url, model);
  }

  update(model) {
    const url = `${this.BASE_URL}/user`;
    return this.http.put(url, model);
  }

  updateUser(user: User) {
    this.userDataSource.next(user);
  }

  updateLanguage(l: string) {
    this.languageSource.next(l);
  }

  getUserLocal(): User {
    return this.userDataSource.value;
  }

  delete(id) {
    const url = `${this.BASE_URL}/user/${id}`;
    return this.http.delete(url);
  }

  updateNotification(id) {
    const url = `${this.BASE_URL}/user/notification/${id}`;
    return this.http.put(url, {});
  }

  upload(file, id, path) {
    const url = `${this.BASE_URL}/upload/${path}/${id}`;
    return new Observable((observer) => {
      const formData: FormData = new FormData(),
        xhr: XMLHttpRequest = new XMLHttpRequest();
      if (file != null) {
        formData.append("file", file, file.name);
      }
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            observer.next(JSON.parse(xhr.response));
            observer.complete();
          } else {
            observer.error(xhr.response);
          }
        }
      };
      xhr.upload.onprogress = (event) => {
        const progress = Math.round((event.loaded / event.total) * 100);
      };
      xhr.open("POST", url, true);
      xhr.setRequestHeader("Authorization", `Bearer ${this.getToken()}`);
      xhr.send(formData);
    });
  }

  getAllUsers(): Observable<GetUsersQuery> {
    return this.getUsersGQL.watch().valueChanges.pipe(
      map((result) => {
        return result.data;
      }),
      catchError(() => {
        return throwError(() => new Error("Failed to get all users"));
      }),
    );
  }

  getAllUsersByCommittee(id: String): Observable<GetUserByCommitteeQuery> {
    return this.getUserByCommitteeGQL.watch({ id }).valueChanges.pipe(
      map((result: ApolloQueryResult<GetUserByCommitteeQuery>) => {
        return result.data;
      }),
      catchError(() => {
        return throwError(() => new Error("Failed to get all users"));
      }),
    );
  }
}
